import React from 'react';
import { View, Text, ScrollView } from 'react-native';

import { Element } from '@components/frame';
import { detectEnvironment } from '@api';
import TouchableElement from '@components/common/touchableElement';
import { NetworkTabProps, RequestInfo } from './types';
import useFetchRequest from './useFetchRequest';
import useXhrRequest from './useXhrRequest';
import useAxiosRequest from './useAxiosRequest';
import { getSecureValue, navigateTo } from '@helpers';
import useDispatchRequest from './useDispatchRequest';

const getEndpoint = async () => {
  const env = detectEnvironment();
  const envList: Record<string, string> = {
    dev: 'gateway-dev',
    qa: 'gateway-qa',
    stg: 'gateway-stg',
    prod: 'gateway',
  };

  let baseENV = envList[process.env.ENV || 'dev'];
  if (env && envList[env]) {
    baseENV = envList[env];
  }
  const baseURL = `https://${baseENV}.smarfac.com/api/v1/machines/availableMachines`;

  return baseURL;
};

const NetworkTab: React.FC<NetworkTabProps> = ({ C, scale }) => {
  const [fetchRequest, sendFetchRequest] = useFetchRequest();
  const [xhrRequest, sendXhrRequest] = useXhrRequest();
  const [axiosRequest, sendAxiosRequest] = useAxiosRequest();
  const [dispatchRequest, sendDispatchRequest] = useDispatchRequest();

  const handleSend = async () => {
    const url = await getEndpoint();
    const token = getSecureValue('token');
    const appHubID = getSecureValue('appHubID');
    const onboardingHubId = getSecureValue('signalRAnonymID');

    const requestHeaders = {
      Authorization: `Bearer ${token}`,
      'Apphub-Connection-Id': appHubID,
      'Onboardinghub-Connection-Id': onboardingHubId,
      'Content-Type': 'application/json',
    };

    await Promise.allSettled([
      sendFetchRequest(url, requestHeaders),
      sendXhrRequest(url, requestHeaders),
      sendAxiosRequest(url, requestHeaders),
      sendDispatchRequest(url),
    ]);
  };

  const renderRequestInfo = (request: RequestInfo | null, title: string) => (
    <View
      style={{
        width: '48%',
        backgroundColor: C('Gray/Light/6'),
        borderRadius: scale(8),
        padding: scale(12),
        marginBottom: scale(10),
        borderWidth: scale(1),
        borderStyle: 'solid',
        borderColor: C('Hairline/Light/No trans [mobile]'),
      }}
    >
      <Text
        style={{
          fontSize: scale(16),
          fontWeight: 600,
          color: C('Label/Light/Primary'),
          marginBottom: scale(8),
        }}
      >
        {title}
      </Text>

      {!request ? (
        <Text
          style={{
            fontSize: scale(13),
            color: C('Label/Light/Primary'),
          }}
        >
          Not sent yet
        </Text>
      ) : (
        <View>
          <Text
            style={{
              fontSize: scale(12),
              color: C('Label/Light/Primary'),
              marginBottom: scale(4),
            }}
          >
            Method: {request.method}
          </Text>

          <Text
            style={{
              fontSize: scale(12),
              color: C('Label/Light/Primary'),
              marginBottom: scale(8),
            }}
          >
            URL: {request.url}
          </Text>

          <Text
            style={{
              fontSize: scale(13),
              fontWeight: '600',
              color: request.status === 'pending' ? C('warning') : request.status === 'completed' ? C('success') : C('error'),
              marginBottom: scale(8),
            }}
          >
            Status: {request.status.toUpperCase()}
          </Text>

          {request.status === 'completed' && (
            <View>
              <Text
                style={{
                  fontSize: scale(12),
                  color: C('Label/Light/Primary'),
                  marginBottom: scale(4),
                }}
              >
                Status Code: {request.statusCode} {request.statusText}
              </Text>

              <Text
                style={{
                  fontSize: scale(12),
                  color: C('Label/Light/Primary'),
                  marginBottom: scale(4),
                }}
              >
                Response Time: {request.responseTime?.toFixed(2)}ms
              </Text>

              <Text
                style={{
                  fontSize: scale(12),
                  color: C('Label/Light/Primary'),
                  marginBottom: scale(8),
                }}
              >
                Response Size: {request.responseSize} bytes
              </Text>

              {request.headers && Object.keys(request.headers).length > 0 && (
                <View>
                  <Text
                    style={{
                      fontSize: scale(13),
                      fontWeight: '600',
                      color: C('Label/Light/Primary'),
                      marginTop: scale(8),
                      marginBottom: scale(6),
                    }}
                  >
                    Response Headers:
                  </Text>
                  {Object.entries(request.headers).map(([key, value]) => (
                    <Text
                      key={key}
                      style={{
                        fontSize: scale(11),
                        color: C('Label/Light/Primary'),
                        marginBottom: scale(2),
                      }}
                    >
                      {key}: {value}
                    </Text>
                  ))}
                </View>
              )}

              {request.responseBody && (
                <View>
                  <Text
                    style={{
                      fontSize: scale(13),
                      fontWeight: '600',
                      color: C('Label/Light/Primary'),
                      marginTop: scale(12),
                      marginBottom: scale(6),
                    }}
                  >
                    Response Body:
                  </Text>
                  <ScrollView
                    style={{
                      maxHeight: scale(200),
                      backgroundColor: C('background'),
                      padding: scale(8),
                      borderRadius: scale(4),
                    }}
                  >
                    <Text
                      style={{
                        fontSize: scale(11),
                        color: C('Label/Light/Primary'),
                      }}
                    >
                      {request.responseBody}
                    </Text>
                  </ScrollView>
                </View>
              )}
            </View>
          )}

          {request.status === 'error' && request.error && (
            <Text
              style={{
                fontSize: scale(12),
                color: C('error'),
                marginTop: scale(4),
              }}
            >
              Error: {request.error}
            </Text>
          )}
        </View>
      )}
    </View>
  );

  return (
    <Element
      {...{
        config: {
          width: '100%',
          paddingLeft: 10,
          paddingRight: 10,
          position: {
            top: 162,
          },
          flexDirection: 'column',
          flexWrap: 'wrap',
        },
      }}
    >
      <TouchableElement onPress={() => navigateTo('StressTest')}>
        {({ boxStyle, pressHoverColor }) => {
          return (
            <View
              style={[
                boxStyle,
                {
                  width: scale(140),
                  marginBottom: scale(20),
                  marginHorizontal: 'auto',
                  paddingVertical: scale(12),
                  paddingHorizontal: scale(32),
                  borderRadius: scale(8),
                  backgroundColor: C(pressHoverColor('Selected/Light/Primary')),
                },
              ]}
            >
              <Text
                style={{
                  fontSize: scale(14),
                  fontWeight: 600,
                  color: C('Button/Light/Primary Text'),
                  textAlign: 'center',
                }}
              >
                Stress Test
              </Text>
            </View>
          );
        }}
      </TouchableElement>
      <TouchableElement onPress={handleSend}>
        {({ boxStyle, pressHoverColor }) => {
          return (
            <View
              style={[
                boxStyle,
                {
                  width: scale(100),
                  marginBottom: scale(20),
                  marginHorizontal: 'auto',
                  paddingVertical: scale(12),
                  paddingHorizontal: scale(32),
                  borderRadius: scale(8),
                  backgroundColor: C(pressHoverColor('Selected/Light/Primary')),
                },
              ]}
            >
              <Text
                style={{
                  fontSize: scale(14),
                  fontWeight: 600,
                  color: C('Button/Light/Primary Text'),
                }}
              >
                Send
              </Text>
            </View>
          );
        }}
      </TouchableElement>

      <ScrollView
        style={{
          width: '100%',
          maxHeight: scale(450),
          marginBottom: scale(60),
        }}
        contentContainerStyle={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: scale(12),
        }}
      >
        {renderRequestInfo(fetchRequest, 'Fetch API')}
        {renderRequestInfo(xhrRequest, 'XMLHttpRequest')}
        {renderRequestInfo(axiosRequest, 'axios API')}
        {renderRequestInfo(dispatchRequest, 'dispatch request')}
      </ScrollView>
    </Element>
  );
};

export default NetworkTab;
